/**
 * Unified file fetching system that handles all file types consistently
 * Supports Local, Hugging Face, and GitHub sources with configurable precedence
 */

// Environment variables
const GITHUB_TOKEN = import.meta.env.VITE_GITHUB_TOKEN
const GITHUB_USERNAME = import.meta.env.VITE_GITHUB_USERNAME || 'TianyiQ'
const GITHUB_REPO = import.meta.env.VITE_GITHUB_REPO || 'Eval-Reasoning-Consistency'

const HF_TOKEN = import.meta.env.VITE_HF_TOKEN || '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'
const HF_USERNAME = import.meta.env.VITE_HF_USERNAME || 'TianyiQ'
const HF_DATASET = import.meta.env.VITE_HF_DATASET || 'TruthSeekingGym-Data'

// Access control flags (all default to false, meaning enabled)
const DISABLE_LOCAL = import.meta.env.VITE_DISABLE_LOCAL === 'true'
const DISABLE_HF = import.meta.env.VITE_DISABLE_HF === 'true'
const DISABLE_GITHUB = import.meta.env.VITE_DISABLE_GITHUB === 'true'

export interface FetchResult {
  content: string | Blob
  url: string
  source: 'local' | 'hf-lfs' | 'hf-api' | 'github-api' | 'github-raw'
  mimeType?: string
}

export interface FetchAttempt {
  method: string
  url: string
  error: string
  status?: number
}

export class DetailedFetchError extends Error {
  constructor(
    message: string,
    public readonly attempts: FetchAttempt[]
  ) {
    super(message)
    this.name = 'DetailedFetchError'
  }
}

/**
 * Convert a catalog path to HF dataset path by removing the 'data/' prefix
 */
function pathToHFPath(path: string): string {
  // Remove 'data/' prefix if present for HF paths
  if (path.startsWith('data/')) {
    return path.substring(5)
  }
  return path
}

/**
 * Fetch file from Hugging Face Hub using the datasets API
 * Supports both LFS and non-LFS files
 */
async function fetchFromHuggingFace(path: string, isLFS: boolean = true): Promise<FetchResult | null> {
  const hfPath = pathToHFPath(path)
  
  // Construct the HF URL based on whether we're trying LFS or regular API
  let hfUrl: string
  if (isLFS) {
    // LFS URL pattern: direct file access through resolve endpoint
    hfUrl = `https://huggingface.co/datasets/${HF_USERNAME}/${HF_DATASET}/resolve/main/${hfPath}`
  } else {
    // Non-LFS: use raw endpoint for smaller files
    hfUrl = `https://huggingface.co/datasets/${HF_USERNAME}/${HF_DATASET}/raw/main/${hfPath}`
  }
  
  console.log(`🤗 Fetching from Hugging Face (${isLFS ? 'LFS' : 'API'}):`, hfUrl)
  
  try {
    const headers: HeadersInit = {
      'User-Agent': 'TruthSeekingGym-Web-App'
    }
    
    // Add authorization if available (for private repos)
    if (HF_TOKEN) {
      headers['Authorization'] = `Bearer ${HF_TOKEN}`
    }
    
    const response = await fetch(hfUrl, { headers })
    
    if (!response.ok) {
      // Check if it's a redirect (common for LFS files)
      if (response.status === 302 || response.status === 301) {
        console.log('🔄 Following HF redirect...')
        // The browser will handle redirects automatically, but log for debugging
      }
      throw new Error(`HF ${isLFS ? 'LFS' : 'API'} failed: ${response.status} ${response.statusText}`)
    }
    
    // Determine file type from path
    const ext = path.split('.').pop()?.toLowerCase() || ''
    const isImage = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(ext)
    const isPdf = ext === 'pdf'
    const isBinary = isImage || isPdf || ['zip', 'gz', 'tar', '7z'].includes(ext)
    
    if (isBinary) {
      // Handle binary files
      const blob = await response.blob()
      
      // Determine MIME type
      let mimeType = 'application/octet-stream'
      if (isImage) {
        mimeType = `image/${ext === 'jpg' ? 'jpeg' : ext === 'svg' ? 'svg+xml' : ext}`
      } else if (isPdf) {
        mimeType = 'application/pdf'
      }
      
      // Create blob URL for consistent handling
      const blobUrl = URL.createObjectURL(blob)
      
      return {
        content: blob,
        url: blobUrl,
        source: isLFS ? 'hf-lfs' : 'hf-api',
        mimeType
      }
    } else {
      // Handle text files
      const text = await response.text()
      return {
        content: text,
        url: hfUrl,
        source: isLFS ? 'hf-lfs' : 'hf-api'
      }
    }
  } catch (error) {
    console.error(`HF ${isLFS ? 'LFS' : 'API'} fetch error:`, error)
    return null
  }
}

/**
 * Alternative HF fetch using the Hub API for file metadata and content
 * This can handle files that aren't directly accessible via resolve/raw
 */
async function fetchFromHFHub(path: string): Promise<FetchResult | null> {
  const hfPath = pathToHFPath(path)
  
  // Use the Hub API to get file info
  const apiUrl = `https://huggingface.co/api/datasets/${HF_USERNAME}/${HF_DATASET}/tree/main`
  
  console.log('🤗 Fetching file list from HF Hub API...')
  
  try {
    const headers: HeadersInit = {
      'User-Agent': 'TruthSeekingGym-Web-App'
    }
    
    if (HF_TOKEN) {
      headers['Authorization'] = `Bearer ${HF_TOKEN}`
    }
    
    const response = await fetch(apiUrl, { headers })
    
    if (!response.ok) {
      throw new Error(`HF Hub API failed: ${response.status}`)
    }
    
    const files = await response.json()
    
    // Find the specific file
    const fileInfo = files.find((f: any) => f.path === hfPath)
    
    if (!fileInfo) {
      throw new Error(`File not found in HF dataset: ${hfPath}`)
    }
    
    // Check if it's an LFS file
    if (fileInfo.lfs) {
      // Use the LFS pointer information
      const lfsUrl = `https://huggingface.co/datasets/${HF_USERNAME}/${HF_DATASET}/resolve/main/${hfPath}`
      console.log('🔄 File is LFS, fetching from:', lfsUrl)
      return fetchFromHuggingFace(path, true)
    } else {
      // Regular file, use raw endpoint
      console.log('📄 File is regular, fetching from raw endpoint')
      return fetchFromHuggingFace(path, false)
    }
  } catch (error) {
    console.error('HF Hub API error:', error)
    return null
  }
}

/**
 * Unified fetch function that handles all file types consistently
 * 
 * Precedence order (when all enabled):
 * 1. Local fetch methods
 * 2. HF LFS fetch methods
 * 3. HF non-LFS fetch methods  
 * 4. GitHub non-LFS fetch methods
 */
export async function unifiedFetch(path: string): Promise<FetchResult> {
  console.log('🔄 UnifiedFetch for:', path)
  
  const attempts: FetchAttempt[] = []
  
  // Determine file type for all methods
  const ext = path.split('.').pop()?.toLowerCase() || ''
  const isImage = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(ext)
  const isPdf = ext === 'pdf'
  const isBinary = isImage || isPdf || ['zip', 'gz', 'tar', '7z'].includes(ext)
  
  // LEVEL 1: Local fetch (highest priority)
  if (!DISABLE_LOCAL) {
    // Special handling for catalog file which might be in public root
    let localUrl: string
    if (path === 'data/scratchpad/results_catalog_populated.json') {
      // Catalog is special - it's in the public root directory
      localUrl = '/results_catalog_populated.json'
    } else {
      // Regular files - strip 'data/' prefix and add '/data/' prefix for local serving
      const cleanPath = path.startsWith('data/') ? path.substring(5) : path
      localUrl = `/data/${cleanPath}`
    }
    console.log('💾 Trying local file:', localUrl)
    
    try {
      const response = await fetch(localUrl)
      if (response.ok) {
        // Check if we got HTML when we didn't expect it (likely the SPA index.html)
        const contentType = response.headers.get('content-type') || ''
        const isHtmlResponse = contentType.includes('text/html')
        
        // For non-HTML files, receiving HTML means we got the fallback index page
        if (isHtmlResponse && ext !== 'html' && ext !== 'htm') {
          // This is likely the SPA fallback page, not the actual file
          const text = await response.text()
          // Check for telltale signs of the index.html
          if (text.includes('<!doctype html>') || text.includes('<div id=root>') || text.includes('/@vite/client')) {
            console.warn('⚠️ Local fetch returned index.html instead of actual file, trying next source...')
            attempts.push({
              method: 'Local File',
              url: localUrl,
              error: 'Received index.html placeholder instead of actual file',
              status: response.status
            })
            // Don't return, continue to next source
          } else {
            // It's HTML but not the index page, might be valid content
            return { content: text, url: localUrl, source: 'local' }
          }
        } else if (isBinary) {
          const blob = await response.blob()
          // Additional check: if we expect binary but got text/html, it's wrong
          if (blob.type.includes('text/html')) {
            console.warn('⚠️ Expected binary file but got HTML, trying next source...')
            attempts.push({
              method: 'Local File',
              url: localUrl,
              error: 'Expected binary content but received HTML',
              status: response.status
            })
            // Don't return, continue to next source
          } else {
            return { 
              content: blob, 
              url: localUrl, 
              source: 'local', 
              mimeType: response.headers.get('content-type') || undefined 
            }
          }
        } else {
          const text = await response.text()
          // For text files, check if we got the index.html by mistake
          if (text.includes('<!doctype html>') && text.includes('<div id=root>') && ext !== 'html') {
            console.warn('⚠️ Local fetch returned index.html for non-HTML file, trying next source...')
            attempts.push({
              method: 'Local File',
              url: localUrl,
              error: 'Received index.html placeholder instead of actual file content',
              status: response.status
            })
            // Don't return, continue to next source
          } else {
            return { content: text, url: localUrl, source: 'local' }
          }
        }
      } else {
        attempts.push({
          method: 'Local File',
          url: localUrl,
          error: `HTTP ${response.status}`,
          status: response.status
        })
      }
    } catch (err) {
      attempts.push({
        method: 'Local File',
        url: localUrl,
        error: err instanceof Error ? err.message : String(err)
      })
    }
  }
  
  // LEVEL 2: Hugging Face LFS (for large files)
  if (!DISABLE_HF) {
    try {
      const hfLfsResult = await fetchFromHuggingFace(path, true)
      if (hfLfsResult) {
        console.log('✅ Successfully fetched from HF LFS')
        return hfLfsResult
      }
    } catch (err) {
      const hfPath = pathToHFPath(path)
      attempts.push({
        method: 'Hugging Face LFS',
        url: `https://huggingface.co/datasets/${HF_USERNAME}/${HF_DATASET}/resolve/main/${hfPath}`,
        error: err instanceof Error ? err.message : String(err)
      })
    }
    
    // LEVEL 3: Hugging Face non-LFS (for smaller files)
    try {
      const hfApiResult = await fetchFromHuggingFace(path, false)
      if (hfApiResult) {
        console.log('✅ Successfully fetched from HF API')
        return hfApiResult
      }
    } catch (err) {
      const hfPath = pathToHFPath(path)
      attempts.push({
        method: 'Hugging Face API',
        url: `https://huggingface.co/datasets/${HF_USERNAME}/${HF_DATASET}/raw/main/${hfPath}`,
        error: err instanceof Error ? err.message : String(err)
      })
    }
    
    // Try Hub API as alternative HF method
    try {
      const hfHubResult = await fetchFromHFHub(path)
      if (hfHubResult) {
        console.log('✅ Successfully fetched via HF Hub API')
        return hfHubResult
      }
    } catch (err) {
      attempts.push({
        method: 'Hugging Face Hub API',
        url: `https://huggingface.co/api/datasets/${HF_USERNAME}/${HF_DATASET}`,
        error: err instanceof Error ? err.message : String(err)
      })
    }
  }
  
  // LEVEL 4: GitHub (lowest priority)
  if (!DISABLE_GITHUB && GITHUB_TOKEN) {
    const githubApiUrl = `https://api.github.com/repos/${GITHUB_USERNAME}/${GITHUB_REPO}/contents/${path}`
    console.log('🌐 Trying GitHub API:', githubApiUrl)
    
    try {
      // GitHub REST API
      const response = await fetch(githubApiUrl, {
        headers: {
          'Accept': 'application/vnd.github.v3+json',
          'Authorization': `token ${GITHUB_TOKEN}`,
          'User-Agent': 'ERC-Results-Web-App'
        }
      })
      
      if (response.ok) {
        const data = await response.json()
        
        if (data.content) {
          const base64Content = data.content.replace(/\n/g, '')
          
          if (isBinary) {
            // Convert base64 to Blob for binary files
            const binaryString = atob(base64Content)
            const bytes = new Uint8Array(binaryString.length)
            for (let i = 0; i < binaryString.length; i++) {
              bytes[i] = binaryString.charCodeAt(i)
            }
            
            let mimeType = 'application/octet-stream'
            if (isImage) {
              mimeType = `image/${ext === 'jpg' ? 'jpeg' : ext === 'svg' ? 'svg+xml' : ext}`
            } else if (isPdf) {
              mimeType = 'application/pdf'
            }
            
            const blob = new Blob([bytes], { type: mimeType })
            const blobUrl = URL.createObjectURL(blob)
            
            return { 
              content: blob, 
              url: blobUrl, 
              source: 'github-api',
              mimeType 
            }
          } else {
            // Decode text
            try {
              const decodedText = atob(base64Content)
              try {
                const utf8Text = decodeURIComponent(escape(decodedText))
                return { 
                  content: utf8Text, 
                  url: githubApiUrl, 
                  source: 'github-api' 
                }
              } catch {
                return { 
                  content: decodedText, 
                  url: githubApiUrl, 
                  source: 'github-api' 
                }
              }
            } catch (decodeError) {
              throw new Error(`Failed to decode base64: ${decodeError}`)
            }
          }
        }
      } else {
        attempts.push({
          method: 'GitHub REST API',
          url: githubApiUrl,
          error: `HTTP ${response.status}`,
          status: response.status
        })
      }
    } catch (err) {
      attempts.push({
        method: 'GitHub REST API',
        url: githubApiUrl,
        error: err instanceof Error ? err.message : String(err)
      })
    }
    
    // Try GitHub GraphQL for larger files
    console.log('🔄 Trying GitHub GraphQL API...')
    try {
      const graphqlResult = await fetchViaGraphQL(path, isBinary)
      if (graphqlResult) {
        return graphqlResult
      }
    } catch (err) {
      attempts.push({
        method: 'GitHub GraphQL API',
        url: 'https://api.github.com/graphql',
        error: err instanceof Error ? err.message : String(err)
      })
    }
    
    // Try GitHub raw URL as last resort
    const githubRawUrl = `https://raw.githubusercontent.com/${GITHUB_USERNAME}/${GITHUB_REPO}/main/${path}`
    console.log('🔄 Trying GitHub Raw URL...')
    
    try {
      const rawResponse = await fetch(githubRawUrl)
      if (rawResponse.ok) {
        if (isBinary) {
          const blob = await rawResponse.blob()
          const blobUrl = URL.createObjectURL(blob)
          return {
            content: blob,
            url: blobUrl,
            source: 'github-raw',
            mimeType: isPdf ? 'application/pdf' : isImage ? `image/${ext}` : 'application/octet-stream'
          }
        } else {
          const content = await rawResponse.text()
          return {
            content,
            url: githubRawUrl,
            source: 'github-raw'
          }
        }
      } else {
        attempts.push({
          method: 'GitHub Raw URL',
          url: githubRawUrl,
          error: `HTTP ${rawResponse.status}`,
          status: rawResponse.status
        })
      }
    } catch (err) {
      attempts.push({
        method: 'GitHub Raw URL',
        url: githubRawUrl,
        error: err instanceof Error ? err.message : String(err)
      })
    }
  }
  
  // All methods failed
  const enabledMethods = [
    !DISABLE_LOCAL ? 'Local' : null,
    !DISABLE_HF ? 'HuggingFace' : null,
    !DISABLE_GITHUB ? 'GitHub' : null
  ].filter(Boolean).join(', ')
  
  throw new DetailedFetchError(
    `Failed to fetch file from any source. Enabled methods: ${enabledMethods}. Total attempts: ${attempts.length}`,
    attempts
  )
}

/**
 * Get a URL for direct browser access (for img src, embed src, etc.)
 * Tries to return the most reliable direct URL based on available sources
 */
export function getDirectUrl(path: string): string {
  // Prefer local if available
  if (!DISABLE_LOCAL) {
    const cleanPath = path.startsWith('data/') ? path.substring(5) : path
    return `/data/${cleanPath}`
  }
  
  // Then try HF
  if (!DISABLE_HF) {
    const hfPath = pathToHFPath(path)
    // Use resolve endpoint for direct access
    return `https://huggingface.co/datasets/${HF_USERNAME}/${HF_DATASET}/resolve/main/${hfPath}`
  }
  
  // Finally GitHub raw
  if (!DISABLE_GITHUB) {
    return `https://raw.githubusercontent.com/${GITHUB_USERNAME}/${GITHUB_REPO}/main/${path}`
  }
  
  // Fallback to local even if disabled (better than nothing)
  const cleanPath = path.startsWith('data/') ? path.substring(5) : path
  return `/data/${cleanPath}`
}

/**
 * Clean up blob URLs to prevent memory leaks
 */
export function cleanupBlobUrl(url: string): void {
  if (url && url.startsWith('blob:')) {
    URL.revokeObjectURL(url)
  }
}

/**
 * Fetch file via GitHub GraphQL API (supports larger files)
 */
async function fetchViaGraphQL(path: string, isBinary: boolean): Promise<FetchResult | null> {
  if (!GITHUB_TOKEN) {
    throw new Error('GitHub token not available for GraphQL query')
  }
  
  const query = `
    query {
      repository(owner: "${GITHUB_USERNAME}", name: "${GITHUB_REPO}") {
        object(expression: "main:${path}") {
          ... on Blob {
            text
            byteSize
            isBinary
          }
        }
      }
    }
  `
  
  try {
    const response = await fetch('https://api.github.com/graphql', {
      method: 'POST',
      headers: {
        'Authorization': `bearer ${GITHUB_TOKEN}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ query })
    })
    
    if (!response.ok) {
      throw new Error(`GraphQL API failed: ${response.status}`)
    }
    
    const result = await response.json()
    
    if (result.errors) {
      throw new Error(`GraphQL errors: ${result.errors.map((e: any) => e.message).join(', ')}`)
    }
    
    const obj = result?.data?.repository?.object
    
    if (!obj) {
      const repoExists = result?.data?.repository !== null
      if (!repoExists) {
        throw new Error('Repository not found')
      }
      throw new Error(`File not found at path: ${path}`)
    }
    
    if (obj.isBinary) {
      throw new Error(`Binary file (size: ${obj.byteSize} bytes) - GraphQL cannot fetch binary content`)
    }
    
    if (!obj.text && obj.text !== '') {
      throw new Error(`No text content (size: ${obj.byteSize} bytes)`)
    }
    
    return {
      content: obj.text,
      url: `https://github.com/${GITHUB_USERNAME}/${GITHUB_REPO}/blob/main/${path}`,
      source: 'github-api'
    }
  } catch (error) {
    console.error('GraphQL fetch error:', error)
    return null
  }
}